import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_META, PRIORITY_ORDER } from "@/lib/meta";
import { useStatuses } from "@/context/StatusContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Search, Upload, Pencil, Paperclip, X } from "lucide-react";
import BulkUpload from "@/components/BulkUpload";
import JobTimer from "@/components/JobTimer";
import { UnassignedJobs } from "@/components/UnassignedJobs";

const ATTACH_MAX_BYTES = 15 * 1024 * 1024;
const fmtSize = (b) =>
  b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`;

const JOB_CSV_TEMPLATE =
  "title,description,rigger_email,priority,status,due_date,location,department,customer,sap_id,bricert_id\n" +
  "Install tower crane,Assemble crane at site,rigger@example.com,high,pending,2026-08-15,Site A,Operations,Acme Corp,SAP-1001,BR-101\n";


const emptyForm = {
  title: "",
  description: "",
  assigned_to: "",
  status: "pending",
  priority: "medium",
  priority_order: "",
  due_date: "",
  location: "",
  department: "",
  customer: "",
  sap_id: "",
  bricert_id: "",
};

export default function Jobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { metaFor, orderedKeys } = useStatuses();
  const canManage = user.role === "admin" || user.role === "manager";
  const canSeeTimer = user.role === "admin" || user.role === "superadmin";
  const isDispatcher = user.role === "job_dispatcher";
  const canCreate = canManage || isDispatcher;

  const [jobs, setJobs] = useState([]);
  const [riggers, setRiggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingDragOver, setPendingDragOver] = useState(false);
  const [createProgress, setCreateProgress] = useState(null);
  const isRigger = user.role === "rigger";
  const [tab, setTab] = useState("mine");
  const [unassigned, setUnassigned] = useState([]);
  const loadUnassigned = useCallback(() => api.get("/jobs/unassigned").then((r) => setUnassigned(r.data)).catch(() => {}), []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/jobs").then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, []);

  const loadLocations = useCallback(() => api.get("/my-locations").then((r) => setLocations(r.data)), []);
  const loadDepartments = useCallback(() => api.get("/departments").then((r) => setDepartments(r.data)), []);

  useEffect(() => {
    load();
    loadLocations();
    loadDepartments();
    if (canManage) api.get("/riggers").then((r) => setRiggers(r.data));
    if (isRigger) loadUnassigned();
  }, [load, loadLocations, loadDepartments, canManage, isRigger, loadUnassigned]);

  const openCreate = () => {
    setForm({ ...emptyForm, status: orderedKeys()[0] || "pending" });
    setEditId(null);
    setPendingFiles([]);
    setOpen(true);
  };

  const openEdit = (j) => {
    setForm({
      title: j.title || "", description: j.description || "", assigned_to: j.assigned_to || "",
      status: j.status || "pending", priority: j.priority || "medium", due_date: j.due_date || "",
      priority_order: j.priority_order ?? "",
      location: j.location || "", department: j.department || "", customer: j.customer || "", sap_id: j.sap_id || "",
      bricert_id: j.bricert_id || "",
    });
    setEditId(j.id);
    setOpen(true);
  };

  const stagePendingFile = (file) => {
    if (!file) return;
    if (pendingFiles.length >= 2) {
      toast.error("You can attach at most 2 files");
      return;
    }
    const used = pendingFiles.reduce((s, f) => s + f.size, 0);
    if (used + file.size > ATTACH_MAX_BYTES) {
      toast.error("Attachments exceed the 15 MB total limit");
      return;
    }
    setPendingFiles((prev) => [...prev, file]);
  };

  const addPendingFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    stagePendingFile(file);
  };

  const onPendingDrop = (e) => {
    e.preventDefault();
    setPendingDragOver(false);
    stagePendingFile(e.dataTransfer?.files?.[0]);
  };

  const removePendingFile = (i) => setPendingFiles(pendingFiles.filter((_, idx) => idx !== i));

  const submitJob = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = isDispatcher
        ? {
            title: form.title, description: form.description, priority: form.priority,
            due_date: form.due_date, location: form.location, department: form.department,
            customer: form.customer, sap_id: form.sap_id,
          }
        : { ...form, priority_order: form.priority_order === "" || form.priority_order === null ? null : Number(form.priority_order) };
      if (editId) {
        await api.put(`/jobs/${editId}`, payload);
        toast.success("Job updated");
      } else {
        const res = await api.post("/jobs", payload);
        const newId = res.data.id;
        const total = pendingFiles.length;
        if (total) setCreateProgress(0);
        for (let i = 0; i < total; i++) {
          const f = pendingFiles[i];
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/jobs/${newId}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (e) => {
                const frac = e.loaded / (e.total || f.size || 1);
                setCreateProgress(Math.round(((i + frac) / total) * 100));
              },
            });
          } catch (err) {
            toast.error(`${f.name}: ${apiError(err.response?.data?.detail)}`);
          }
        }
        setCreateProgress(null);
        toast.success("Job created");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditId(null);
      setPendingFiles([]);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const quickAssign = async (jobId, riggerId) => {
    if (!riggerId) return;
    try {
      await api.put(`/jobs/${jobId}`, { assigned_to: riggerId });
      const r = riggers.find((x) => x.id === riggerId);
      toast.success(`Assigned to ${r?.name || "rigger"}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/jobs/${jobId}`, { assigned_to: "" });
              toast.success("Assignment removed");
              load();
            } catch (e) {
              toast.error(apiError(e.response?.data?.detail));
            }
          },
        },
      });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/jobs/${deleteId}`);
      toast.success("Job deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const filtered = jobs.filter((j) => {
    const q = query.toLowerCase();
    const matchQ =
      !q ||
      j.title.toLowerCase().includes(q) ||
      (j.customer || "").toLowerCase().includes(q) ||
      (j.location || "").toLowerCase().includes(q) ||
      (j.assigned_to_name || "").toLowerCase().includes(q);
    const matchS = statusFilter === "all" || j.status === statusFilter;
    const matchP = priorityFilter === "all" || j.priority === priorityFilter;
    const matchL = locationFilter === "all" || j.location === locationFilter;
    return matchQ && matchS && matchP && matchL;
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">
            Operations
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
            Jobs
          </h1>
        </div>
        {canCreate && (
          <div className="flex gap-3">
            {canManage && (
              <button
                onClick={() => setBulkOpen(true)}
                data-testid="bulk-upload-jobs-button"
                className="bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-none font-bold tracking-tight hover:bg-zinc-200 transition-colors px-5 py-2.5 flex items-center gap-2"
              >
                <Upload size={18} /> Upload CSV
              </button>
            )}
            <button
              onClick={openCreate}
              data-testid="new-job-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
            >
              <Plus size={18} /> New Job
            </button>
          </div>
        )}
      </div>

      {isRigger && (
        <div className="flex border-b border-zinc-200 mb-6" data-testid="rigger-job-tabs">
          {[["mine", "My Jobs", jobs.length], ["unassigned", "Unassigned at my locations", unassigned.length]].map(([k, l, n]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`jobs-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-bold tracking-tight border-b-2 -mb-px transition-colors flex items-center gap-2 ${tab === k ? "border-brand text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              {l}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${tab === k ? "bg-brand text-white" : "bg-zinc-200 text-zinc-600"}`}>{n}</span>
            </button>
          ))}
        </div>
      )}

      {isRigger && tab === "unassigned" ? (
        <UnassignedJobs jobs={unassigned} hasLocations={(user.locations || []).length > 0}
          onClaimed={() => { loadUnassigned(); load(); }} />
      ) : (<>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="job-search"
            placeholder="Search jobs, customers, riggers, locations…"
            className="w-full pl-9 pr-4 py-2.5 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="job-status-filter"
          className="px-4 py-2.5 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="all">All statuses</option>
          {orderedKeys().map((s) => (
            <option key={s} value={s}>
              {metaFor(s).label}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          data-testid="job-priority-filter"
          className="px-4 py-2.5 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="all">All priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          data-testid="job-location-filter"
          className="px-4 py-2.5 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="all">All locations</option>
          {locations.map((l) => (
            <option key={l.id} value={l.name}>
              {l.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Job</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Manual Time (hrs)</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Customer</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Assigned</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Priority</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Priority Order</th>
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Status</th>
                {canSeeTimer && <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Timer</th>}
                <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Due</th>
                {canManage && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr>
                  <td colSpan={(canManage ? 10 : 9) - (canSeeTimer ? 0 : 1)} className="px-4 py-10 text-center text-zinc-400 font-mono text-sm">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={(canManage ? 10 : 9) - (canSeeTimer ? 0 : 1)} className="px-4 py-10 text-center text-zinc-400 text-sm">
                    No jobs found.
                  </td>
                </tr>
              ) : (
                filtered.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => navigate(`/jobs/${j.id}`)}
                    data-testid={`job-row-${j.id}`}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-900">{j.title}</div>
                      {j.location && (
                        <div className="text-xs text-zinc-500">{j.location}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-zinc-700" data-testid={`job-manual-time-${j.id}`}>{j.manual_time_hours != null ? j.manual_time_hours : "—"}</td>
                    <td className="px-4 py-3 text-zinc-700" data-testid={`job-customer-${j.id}`}>{j.customer || "—"}</td>
                    <td className="px-4 py-3 text-zinc-700" onClick={(e) => e.stopPropagation()}>
                      {j.assigned_to_name ? (
                        j.assigned_to_name
                      ) : canManage ? (
                        <select
                          value=""
                          onChange={(e) => quickAssign(j.id, e.target.value)}
                          data-testid={`quick-assign-${j.id}`}
                          className="text-xs border border-dashed border-zinc-300 rounded-full px-2.5 py-1 bg-white text-brand font-semibold hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand cursor-pointer"
                        >
                          <option value="">+ Assign rigger</option>
                          {riggers.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-zinc-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${PRIORITY_META[j.priority].cls}`}>
                        {PRIORITY_META[j.priority].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-zinc-700" data-testid={`job-priority-order-${j.id}`}>{j.priority_order != null ? j.priority_order : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${metaFor(j.status).cls}`}>
                        {metaFor(j.status).label}
                      </span>
                    </td>
                    {canSeeTimer && (
                    <td className="px-4 py-3 text-xs">
                      <JobTimer job={j} />
                    </td>
                    )}
                    <td className="px-4 py-3 text-zinc-600 tabular-nums">
                      {j.due_date || "—"}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(j);
                          }}
                          data-testid={`edit-job-${j.id}`}
                          className="text-zinc-400 hover:text-brand transition-colors mr-3"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(j.id);
                          }}
                          data-testid={`delete-job-${j.id}`}
                          className="text-zinc-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {editId ? "Edit Job" : "New Job"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitJob} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Title</label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="job-title-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Description <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="job-description-input"
                rows={3}
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {!isDispatcher && (
                <div>
                  <label className="text-sm font-bold text-zinc-900 block mb-1">Assign Rigger</label>
                  <select
                    value={form.assigned_to}
                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                    data-testid="job-assign-select"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    <option value="">— Unassigned —</option>
                    {riggers.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  data-testid="job-priority-select"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  {PRIORITY_ORDER.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>
              </div>
              {canManage && (
                <div>
                  <label className="text-sm font-bold text-zinc-900 block mb-1">
                    Priority Order <span className="text-zinc-400 font-normal">(lower = higher priority)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.priority_order}
                    onChange={(e) => setForm({ ...form, priority_order: e.target.value })}
                    data-testid="job-order-input"
                    placeholder="e.g. 1"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Due date</label>
                <input
                  type="date"
                  required
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="job-due-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Location</label>
                <select
                  required
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="job-location-select"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Select location</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.name}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Department</label>
                <select
                  required
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  data-testid="job-department-select"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Select department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Customer</label>
                <input
                  required
                  value={form.customer}
                  onChange={(e) => setForm({ ...form, customer: e.target.value })}
                  data-testid="job-customer-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">SAP ID</label>
                <input
                  required
                  value={form.sap_id}
                  onChange={(e) => setForm({ ...form, sap_id: e.target.value })}
                  data-testid="job-sapid-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              {!isDispatcher && (
                <div className="col-span-2">
                  <label className="text-sm font-bold text-zinc-900 block mb-1">
                    Bricert ID <span className="text-zinc-400 font-normal">(optional)</span>
                  </label>
                  <input
                    value={form.bricert_id}
                    onChange={(e) => setForm({ ...form, bricert_id: e.target.value })}
                    data-testid="job-bricertid-input"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
              )}
            </div>
            {!editId && (
              <div data-testid="new-job-attachments">
                <label className="text-sm font-bold text-zinc-900 block mb-1 flex items-center gap-1.5">
                  <Paperclip size={14} /> Attachments{" "}
                  <span className="text-zinc-400 font-normal">(optional · up to 2, 15 MB total)</span>
                </label>
                <div
                  data-testid="pending-attachments-dropzone"
                  onDragOver={(e) => {
                    if (pendingFiles.length >= 2) return;
                    e.preventDefault();
                    setPendingDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setPendingDragOver(false);
                  }}
                  onDrop={onPendingDrop}
                  className={`border border-dashed rounded-none p-3 space-y-2 transition-colors ${
                    pendingDragOver && pendingFiles.length < 2 ? "border-brand border-2 bg-brand-card-blue" : "border-zinc-300"
                  }`}
                >
                  {pendingFiles.length > 0 && (
                    <div className="space-y-1.5">
                      {pendingFiles.map((f, i) => (
                        <div
                          key={`${f.name}-${i}`}
                          data-testid={`pending-attachment-${i}`}
                          className="flex items-center gap-2 text-sm bg-zinc-50 border border-zinc-200 px-2.5 py-1.5"
                        >
                          <Paperclip size={14} className="text-zinc-400 shrink-0" />
                          <span className="truncate flex-1 text-zinc-800">{f.name}</span>
                          <span className="text-xs text-zinc-400 shrink-0">{fmtSize(f.size)}</span>
                          <button
                            type="button"
                            onClick={() => removePendingFile(i)}
                            data-testid={`remove-pending-attachment-${i}`}
                            className="text-zinc-400 hover:text-red-600 shrink-0"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {pendingFiles.length < 2 && (
                    <label
                      data-testid="add-pending-attachment-label"
                      className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer bg-zinc-100 text-zinc-800 border border-zinc-300 hover:bg-zinc-200 transition-colors"
                    >
                      <Upload size={13} /> Add file
                      <input type="file" className="hidden" onChange={addPendingFile} data-testid="pending-attachment-input" />
                    </label>
                  )}
                  {pendingFiles.length < 2 && (
                    <p className="text-xs text-zinc-400">Drag &amp; drop a file here, or use “Add file”.</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              {createProgress !== null && (
                <div className="w-full mb-2" data-testid="new-job-upload-progress">
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                    <span className="font-semibold">Uploading attachments…</span>
                    <span className="tabular-nums">{createProgress}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand transition-[width] duration-150 ease-out"
                      style={{ width: `${createProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={saving}
                data-testid="job-submit-button"
                className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60"
              >
                {saving ? "Saving…" : editId ? "Save changes" : "Create Job"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-job"
              className="bg-red-600 hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkUpload
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/jobs/bulk"
        templateName="jobs_template.csv"
        templateContent={JOB_CSV_TEMPLATE}
        onDone={load}
        testidPrefix="jobs-bulk"
      />
    </div>
  );
}
